import { Box, Button, Menu, MenuItem, Typography } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { grey } from '@mui/material/colors'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAppDispatch, useAppSelector } from 'redux/Hook'
import { setToolBuilder } from 'redux/slices/ToolBuilder'
import { updateToolState } from 'redux/thunks/ToolBuilder'

const TOOL_STATE_ACTIONS: Record<string, Record<string, string | null>> = {
  draft: {
    published: 'publish',
    archived: null, // invalid transition
  },
  published: {
    draft: 'unpublish',
    archived: 'archive',
  },
  archived: {
    published: 'unarchive',
  },
}

const ToolStateDropdown = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const {
    fetching,
    saving,
    toolBuilder: { id, state = 'draft' },
  } = useAppSelector((state) => state.toolBuilder)

  const isMenuOpen = Boolean(anchorEl)

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const closeMenu = () => {
    setAnchorEl(null)
  }

  const handleStateChange = async (newState: string) => {
    const action = TOOL_STATE_ACTIONS[state]?.[newState]

    if (action === null || (!action && newState !== state)) {
      // Invalid transition
      closeMenu()
      return
    }

    if (action) {
      await dispatch(updateToolState({ state: action }))
    }

    dispatch(setToolBuilder({ name: 'state', value: newState }))
    closeMenu()
  }

  const renderMenuItems = () => {
    const nextStates = TOOL_STATE_ACTIONS[state]

    if (!nextStates) return null

    return Object.entries(nextStates).map(([nextState, action]) => {
      if (action === null) return null // Skip invalid transitions

      return (
        <MenuItem key={nextState} onClick={() => handleStateChange(nextState)}>
          <Typography>{t(`common.${action ?? nextState}`)}</Typography>
        </MenuItem>
      )
    })
  }

  return (
    <Box>
      <Button
        onClick={openMenu}
        disabled={fetching || saving || !id}
        variant="outlined"
        aria-label={t('toolBuilder.aria.toolState', { state })}
        aria-haspopup="true"
        aria-controls="state-menu"
        endIcon={<ArrowDropDownIcon />}
        sx={{
          color: 'white',
          borderColor: grey[700],
          background: grey[700],
        }}
      >
        {t(`common.${state}`)}
      </Button>

      <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={closeMenu}>
        {renderMenuItems()}
      </Menu>
    </Box>
  )
}

export default ToolStateDropdown
